import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../globals";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { brands } from "@fortawesome/fontawesome-svg-core/import.macro";

import HttpManager from "../../services/HttpManager";
import { ProspectCatalogs, rutaWappchatActiva, usarRutaWappchat } from "../../services/v2/prospects.service";
import ClienteView from "../../navigation/v2/sales/clienteView";
import { errorTexto } from "../../navigation/v2/sales/salesHelpers";
import "./whatsapp.css";

/**
 * WhatsApp desde las pantallas de alumnos (Esteban, 2026-10-18: "un símbolo de WhatsApp ... que lance a la persona en WappChat
 * para escribirle", "que tire a modal y/o a otra tab"). Un clic abre la conversación encima de la pantalla (no se pierde lo que
 * estaban haciendo); Ctrl/⌘ + clic, el clic de en medio o "↗" la abren en otra pestaña, para atender varias a la vez.
 */

/** ¿Tiene WappChat (sección 72)? Sin ella el chat no abre (403): ni botón, ni casillas, ni barra. Hoy Académico y RRHH no la tienen. */
export const useVeWappChat = (): boolean => {
	const menuRef: any = useSelector((state: RootState) => (state as any).menuRef?.value);
	return !!menuRef?.wappchat;
};

/** La pestaña se abre en el mismo clic (si se abre después de esperar al servidor, el navegador la bloquea como ventana emergente). */
export const abrirChatEnPestana = async (campusId: number, studentId: number): Promise<string | null> => {
	const w = window.open("about:blank", "_blank");
	try {
		const r = await HttpManager.getInstance().prospectsService.alumnoChat(campusId, studentId);
		if (w) w.location.href = "/wappchat?id=" + r.data.prospect_id + "&dept=" + r.data.dept;
		return null;
	} catch (e) {
		if (w) w.close();
		return errorTexto(e);
	}
};

interface BotonProps { campusId: number; studentId: number; nombre?: string; texto?: boolean; className?: string; }

export const BotonWhatsappAlumno: React.FC<BotonProps> = ({ campusId, studentId, nombre, texto, className }) => {
	const ve = useVeWappChat();
	const [abierto, setAbierto] = useState<boolean>(false);
	const [aviso, setAviso] = useState<string | null>(null);
	const enPestana = async () => { const err = await abrirChatEnPestana(campusId, studentId); if (err) setAviso(err); };
	const clic = (e: React.MouseEvent) => {
		e.stopPropagation();
		// En el celular el chat a pantalla completa es otra pestaña; en la computadora, encima de la pantalla.
		if (e.ctrlKey || e.metaKey || window.innerWidth <= 700) enPestana();
		else setAbierto(true);
	};
	if (!ve) return null;
	return (
		<>
			<button type="button" className={"wa-alumno-btn" + (texto ? " con-texto" : "") + (className ? " " + className : "")} onClick={clic}
				onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); e.stopPropagation(); enPestana(); } }}
				title={"WhatsApp" + (nombre ? " a " + nombre : "") + " (Ctrl/⌘ + clic: otra pestaña)"} aria-label={"WhatsApp" + (nombre ? " a " + nombre : "")}>
				<FontAwesomeIcon icon={brands("whatsapp")} />{texto ? <span>WhatsApp</span> : null}
			</button>
			{aviso ? <span className="wa-alumno-aviso" role="alert" onClick={() => setAviso(null)}>{aviso}</span> : null}
			{abierto ? <ChatFlotante campusId={campusId} studentId={studentId} nombre={nombre} onClose={() => setAbierto(false)} /> : null}
		</>
	);
};

/** La conversación de WappChat en una ventana encima de la pantalla, con "Abrir en otra pestaña". */
export const ChatFlotante: React.FC<{ campusId: number; studentId: number; nombre?: string; onClose: () => void }> = ({ campusId, studentId, nombre, onClose }) => {
	const http = HttpManager.getInstance();
	const [pid, setPid] = useState<number | null>(null);
	const [dept, setDept] = useState<string | null>(null);
	const [catalogs, setCatalogs] = useState<ProspectCatalogs | null>(null);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		let vivo = true;
		// La ficha de la persona por la ruta de WappChat (sección 72): Caja, Kits y Académico no tienen el CRM (sección prospects)
		// y con la ruta del CRM todo daba 403. Se deja como estaba al cerrar. Va antes de cualquier petición.
		const antes = rutaWappchatActiva();
		usarRutaWappchat(true);
		http.prospectsService.alumnoChat(campusId, studentId).then((r) => { if (vivo) { setPid(r.data.prospect_id); setDept(r.data.dept); } })
			.catch((e) => { if (vivo) setError(errorTexto(e)); });
		http.prospectsService.catalogs(campusId).then((r) => { if (vivo) setCatalogs(r.data); }).catch(() => null);
		return () => { vivo = false; usarRutaWappchat(antes); };
	}, [campusId, studentId]);
	return (
		<Modal show onHide={onClose} size="xl" dialogClassName="wa-flotante" className="waModalArriba" backdropClassName="waFondoArriba"
			aria-label={"WhatsApp" + (nombre ? " · " + nombre : "")}>
			<div className="wa-flotante-cab">
				<b>WhatsApp{nombre ? " · " + nombre : ""}</b>
				{pid ? <a className="wa-flotante-tab" href={"/wappchat?id=" + pid + (dept ? "&dept=" + dept : "")} target="_blank" rel="noopener noreferrer">Abrir en otra pestaña ↗</a> : null}
				<button type="button" className="wa-flotante-x" onClick={onClose} aria-label="Cerrar">×</button>
			</div>
			<div className="ventas con-cliente wa-flotante-cuerpo">
				{error ? <div className="aviso error" style={{ margin: 16 }}>{error}</div>
					: pid ? (
						<div className="ventas-cuerpo">
							<ClienteView campusId={campusId} prospectId={pid} catalogs={catalogs} onChanged={() => null} onOpen={setPid} onBack={onClose}
								volverTexto="Cerrar" bandeja={dept ? { dept, channelId: null, supervisa: false } : null} />
						</div>
					) : <div className="wa-flotante-cargando">Abriendo la conversación…</div>}
			</div>
		</Modal>
	);
};
